import type React from 'react';
import { useCallback, useState } from 'react';

import { AutoFixHighRounded } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { OrdCodeInput, OrdFormList, OrdIconStandard, OrdTabbedForm } from '@ragu-components/ord-components';
import { ApTemplateUpdateFields, dataSchema } from '@ragu-components/ord-types';
import { ArrayInput, SimpleFormIterator, TabbedForm, TextInput, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { DefaultTemplateDialog } from './DefaultTemplateDialog';

import type { SxProps } from '@mui/material';
import type { OrdTabbedFormProps } from '@ragu-components/ord-components';

const arrayInputStyle: SxProps = {
  '& .RaArrayInput-label': { marginBottom: 1 },
  '& .RaSimpleFormIterator-line': { borderBottom: 'none !important' },
};

const ApTemplateInput = (
  props: Omit<React.ComponentProps<typeof JsonSchemaInput>, 'jsonSchema' | 'schemas'>
): React.ReactElement => (
  <JsonSchemaInput isRequired jsonSchema={dataSchema.ApTemplateUpdate} schemas={dataSchema} {...props} />
);

const MacRegexInput = (): React.ReactElement => (
  <ApTemplateInput source={ApTemplateUpdateFields['metadata.mac_regex']} fullWidth helperText={false} />
);

const UaPatternInput = (): React.ReactElement => (
  <TextInput source='' label={false} required helperText='resources.ap_templates.helperTexts.ua_patterns' />
);

const MarkupSection = (): React.ReactElement => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(
    (markup: string) => {
      setValue(ApTemplateUpdateFields.markup, markup, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <OrdFormList icon={<OrdIconStandard name='code' />}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Button
          size='small'
          startIcon={<AutoFixHighRounded />}
          onClick={() => {
            setDialogOpen(true);
          }}
        >
          {translate('resources.ap_templates.defaultTemplate.button')}
        </Button>
      </Box>
      <OrdCodeInput source={ApTemplateUpdateFields.markup} language='liquid' fullWidth minRows={10} isRequired />
      <DefaultTemplateDialog open={dialogOpen} onClose={handleClose} onConfirm={handleConfirm} />
    </OrdFormList>
  );
};

export const ApTemplatesForm = (props: OrdTabbedFormProps): React.ReactElement => (
  <OrdTabbedForm
    sanitizeFields
    replaceRelationships
    createJsonSchema={dataSchema.ApTemplateCreate}
    editJsonSchema={dataSchema.ApTemplateUpdate}
    {...props}
  >
    <TabbedForm.Tab label='resources.ap_templates.operations.sections.details'>
      <OrdFormList icon={<OrdIconStandard name='apTemplates' />}>
        <ApTemplateInput source={ApTemplateUpdateFields.name} />
        <TextInput multiline source={ApTemplateUpdateFields.description} />
      </OrdFormList>
      <MarkupSection />
    </TabbedForm.Tab>
    <TabbedForm.Tab label='resources.ap_templates.operations.sections.metadata'>
      <OrdFormList icon={<OrdIconStandard name='apSettings' />}>
        <MacRegexInput />
        <ApTemplateInput source={ApTemplateUpdateFields['metadata.max_accounts']} />
        <ArrayInput
          required
          source={ApTemplateUpdateFields['metadata.ua_patterns']}
          sx={arrayInputStyle}
          defaultValue={['']}
        >
          <SimpleFormIterator fullWidth disableReordering disableClear>
            <UaPatternInput />
          </SimpleFormIterator>
        </ArrayInput>
      </OrdFormList>
    </TabbedForm.Tab>
  </OrdTabbedForm>
);
